import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { newsList } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const PAGE_SIZE = 5;
const filters = ["全部", "热点", "专题", "新番"] as const;

export default function NewsList() {
  const [filter, setFilter] = useState<string>("全部");
  const [sort, setSort] = useState<"time" | "hot">("time");
  const [page, setPage] = useState(1);

  useDocumentTitle(
    "动漫资讯 - 最新动画新闻与专题报道 - 动漫网站",
    "动漫资讯频道聚合最新动画新闻、行业动态与专题报道，及时了解动漫圈的热点话题。"
  );

  const filtered = useMemo(() => {
    const list = newsList.filter((n) => filter === "全部" || n.category === filter);
    return list.sort((a, b) =>
      sort === "time"
        ? new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        : b.views - a.views
    );
  }, [filter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const items = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb items={[{ label: "动漫资讯" }]} />

      <header className="mt-5 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">动漫资讯</h1>
        <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
          最新动画新闻、行业动态与专题报道，掌握动漫圈的第一手资讯。
        </p>
      </header>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilter(f);
                setPage(1);
              }}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                filter === f ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
          <button
            onClick={() => setSort("time")}
            className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              sort === "time" ? "bg-accent-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
            }`}
          >
            按时间
          </button>
          <button
            onClick={() => setSort("hot")}
            className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              sort === "hot" ? "bg-accent-500 text-background-50" : "text-foreground-600 hover:bg-background-50"
            }`}
          >
            按热点
          </button>
        </div>
      </div>

      {items.length > 0 ? (
        <>
          <div className="flex flex-col gap-4">
            {items.map((news) => (
              <Link
                key={news.id}
                to={`/news/${news.id}`}
                className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all"
              >
                <div className="w-full sm:w-56 h-40 sm:h-32 rounded-md overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={news.cover}
                    alt={news.title}
                    title={`${news.title} - 动漫资讯`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900">{news.category}</span>
                    <span className="flex items-center gap-1"><i className="ri-calendar-line" />{news.publishedAt}</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line" />{news.views.toLocaleString()}</span>
                  </div>
                  <h2 className="font-heading font-semibold text-base md:text-lg text-foreground-950 group-hover:text-primary-600 transition-colors mt-2 clamp-2">
                    {news.title}
                  </h2>
                  <p className="text-sm text-foreground-600 mt-2 clamp-2 leading-relaxed">{news.summary}</p>
                  <span className="text-sm text-primary-600 mt-auto pt-2 flex items-center gap-1">
                    阅读全文
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <Pagination page={current} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="py-20 text-center bg-background-100 rounded-lg border border-background-200/70">
          <i className="ri-newspaper-line text-3xl text-foreground-400" />
          <h3 className="font-heading font-semibold text-lg text-foreground-900 mt-4">该分类暂无资讯</h3>
          <button
            onClick={() => {
              setFilter("全部");
              setPage(1);
            }}
            className="mt-5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer"
          >
            查看全部资讯
          </button>
        </div>
      )}
    </div>
  );
}